import { paymentService } from '../../_services/payment.service';
import { hairdresserService } from '../../_services/hairdresser.service';
import { salonService } from '../../_services/salon.service';
import { settingService } from '../../_services/setting.service';
import { IS_MOCK_API } from './onboarding.service';

/**
 * Decision registry (plan §4.3).
 *
 * The "Needs you" queue carries `decision` items keyed by one of these.
 * Each entry owns its copy, its choices and how the answer is written,
 * through the SAME settings endpoint the Settings pages use, so answering
 * here and changing it later in Settings are the same thing.
 *
 *  applies(context, salon) — the server decides what to ask (it sends the
 *  item), but the FE re-checks so a stale item never renders nonsense.
 *  write(value, context)   — persists via existing endpoints. In mock mode
 *  we skip the network so reviewers can click through without a backend.
 */

const noop = () => Promise.resolve();
const guard = (fn) => (IS_MOCK_API ? noop : fn);

const ownerSalonPatch = (patch) => {
  const fd = new FormData();
  fd.append('salonId', localStorage.getItem('salonId') || '');
  Object.keys(patch).forEach((k) => fd.append(k, patch[k]));
  return salonService.updateSalon(fd);
};

export const DECISIONS = {
  card_fees: {
    title: 'Who covers card fees?',
    sub: 'For card reader payments, deposits, cards on file and payment links.',
    applies: (ctx) => (ctx?.selfEmployedCount || 0) > 0,
    choices: [
      { value: 2, label: 'The salon', hint: 'One bill, simplest to run.' },
      { value: 1, label: 'Each self-employed stylist', hint: 'Fees come out of their takings.' },
    ],
    write: guard((value) => paymentService.updateTransactionFee({ transactionFeeType: value })),
    settingsPath: '/settings/card-reader',
  },
  client_sharing: {
    title: 'Share client details with your self-employed team?',
    sub: 'Employed staff always see salon clients. This is about chair renters.',
    applies: (ctx) => (ctx?.selfEmployedCount || 0) > 0,
    choices: [
      { value: 1, label: 'Share', hint: 'They can see and book any salon client.' },
      { value: 0, label: 'Keep separate', hint: 'Each keeps their own list. You can change this any time.' },
    ],
    write: guard((value) => hairdresserService.clientShare({ clientShare: value })),
    settingsPath: '/clients/data-sharing',
  },
  guest_booking: {
    title: 'Let clients book without creating an account?',
    sub: 'They still get a confirmation and a link to manage the booking.',
    applies: () => true,
    choices: [
      { value: true, label: 'Yes, keep it quick', hint: 'Fewer drop-offs at checkout. Recommended.' },
      { value: false, label: 'Ask them to sign up', hint: 'Every booking sits on an account from day one.' },
    ],
    write: guard((value) => ownerSalonPatch({ allowGuestBooking: value })),
    settingsPath: '/settings/booking-url',
  },
  marketplace: {
    title: 'List on the Beu marketplace?',
    sub: 'New clients nearby can find and book you. Applied when you go live.',
    applies: () => true,
    choices: [
      { value: true, label: 'Yes, list us', hint: 'Free. You can unlist any time.' },
      { value: false, label: 'Not yet', hint: 'Your own booking link still works.' },
    ],
    // Stored only; RevealScreen applies it together with onlineBookingEnabled
    // on "Take my bookings live" so nothing is public before the owner says so.
    write: () => Promise.resolve(),
    settingsPath: '/settings/booking-url',
  },
  no_show_payer: {
    title: "If a client doesn't show, who keeps the fee?",
    sub: 'Applies to no-show and late-cancellation charges.',
    applies: (ctx) => !!ctx?.hasPolicy && (ctx?.selfEmployedCount || 0) > 0,
    choices: [
      { value: 2, label: 'The salon', hint: 'Paid into the salon account.' },
      { value: 1, label: 'The stylist', hint: 'Paid to whoever lost the slot.' },
    ],
    write: guard((value) => settingService.updateNoShowProtectionFee({ noShowFeePaidBy: value })),
    settingsPath: '/settings/booking-url',
  },
  consent_carry: {
    title: (ctx) => `Your old system had a "${ctx?.column || 'marketing opt-in'}" column. Carry it over?`,
    sub: (ctx) => (ctx?.optedIn != null ? `${Number(ctx.optedIn).toLocaleString('en-GB')} of ${Number(ctx.total || 0).toLocaleString('en-GB')} clients were opted in.` : 'We never assume consent. You choose.'),
    applies: (ctx) => !!ctx?.column,
    choices: [
      { value: 'carry', label: 'Carry it over', hint: 'Only those marked opted-in can get marketing.' },
      { value: 'none', label: 'Start everyone unsubscribed', hint: 'Safest. Ask again in your first campaign.' },
    ],
    // Applied by the import commit; nothing to write here.
    write: () => Promise.resolve(),
  },
  opening_hours: {
    title: 'We read these opening hours. Right?',
    sub: 'Read from your files. Edit anything that looks off.',
    applies: (ctx) => Array.isArray(ctx?.hours) && ctx.hours.length > 0,
    custom: 'hours', // rendered by DecisionCard as an editable hours table
    write: guard((hours) => salonService.updateOpeningHours(localStorage.getItem('salonId'), hours)),
    settingsPath: '/settings/salon-details',
  },
};

export const decisionFor = (key) => DECISIONS[key] || null;
export const resolveText = (v, ctx) => (typeof v === 'function' ? v(ctx) : v);
